import React, { createContext, useState, useCallback, useContext, useEffect, ReactNode } from 'react';
import Log from '../log';

// Define types
export interface StatusMessage {
  id: number;
  message: string;
  timestamp: number;
}

export type AddStatusFunc = (message: string) => number;
export type RemoveStatusFunc = (id: number) => void;
export type ClearAllStatusesFunc = () => void;

interface LoadingStatusContextType {
  statuses: StatusMessage[];
  addStatus: AddStatusFunc;
  removeStatus: RemoveStatusFunc;
  clearAllStatuses: ClearAllStatusesFunc;
}

interface LoadingStatusProviderProps {
  children: ReactNode;
}

// Create the context with a default value
export const LoadingStatusContext = createContext<LoadingStatusContextType>({
  statuses: [],
  addStatus: (message: string) => { console.warn('LoadingStatusContext not yet initialized'); return 0; },
  removeStatus: (id: number) => { console.warn('LoadingStatusContext not yet initialized'); },
  clearAllStatuses: () => { console.warn('LoadingStatusContext not yet initialized'); }
});

// Create the provider component
export const LoadingStatusProvider: React.FC<LoadingStatusProviderProps> = ({ children }) => {
  const [statuses, setStatuses] = useState<StatusMessage[]>([]);
  const [nextId, setNextId] = useState<number>(1); // Use state for ID generation to ensure uniqueness

  const addStatus = useCallback((message: string): number => {
    const id = Date.now() + nextId; // Generate unique ID
    Log.log(message);
    console.log(`Adding status [${id}]: ${message}`);
    setStatuses(prev => [...prev, { id, message, timestamp: Date.now() }]);
    setNextId(prev => prev + 1); // Increment ID counter
    return id; // Return the ID so the caller can remove it later
  }, [nextId]);

  const removeStatus = useCallback((id: number): void => {
    console.log(`Removing status [${id}]`);
    setStatuses(prev => {
      const filtered = prev.filter(status => status.id !== id);
      
      // If we didn't remove anything, log a warning
      if (filtered.length === prev.length) {
        console.warn(`Status with ID ${id} not found for removal`);
      }
      
      return filtered;
    });
  }, []);

  // Safety cleanup: remove statuses older than 60 seconds
  const clearAllStatuses = useCallback((): void => {
    console.log('Clearing all statuses');
    setStatuses([]);
  }, []);

  // Safety cleanup: remove statuses older than 30 seconds
  useEffect(() => {
    if (statuses.length === 0) return;
    
    const interval = setInterval(() => {
      const now = Date.now();
      setStatuses(prev => {
        const oldStatuses = prev.filter(s => (now - s.timestamp) > 60000);
        if (oldStatuses.length > 0) {
          console.warn('Cleaning up stale statuses:', oldStatuses.map(s => s.id));
          return prev.filter(s => (now - s.timestamp) <= 60000);
        }
        return prev;
      });
    }, 5000);
    
    return () => clearInterval(interval);
  }, [statuses]);

  const contextValue = { 
    statuses, 
    addStatus, 
    removeStatus,
    clearAllStatuses 
  };

  return (
    <LoadingStatusContext.Provider value={contextValue}>
      {children}
    </LoadingStatusContext.Provider>
  );
};

// Create a custom hook for easy consumption
export const useLoadingStatus = (): LoadingStatusContextType => useContext(LoadingStatusContext);
